import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input, Button, Card, CardBody } from '@heroui/react';
import { Lock, Eye, EyeOff, ArrowLeft, Check, X } from 'lucide-react';
import { apiClient } from '@/api/client';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const allRulesPassing = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRulesPassing && passwordsMatch && token.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setIsLoading(true);

    try {
      await apiClient.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(
        axiosError?.response?.data?.message ??
          axiosError?.message ??
          'Something went wrong. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: 'var(--bg-base)' }}
      >
        <div className="w-full max-w-sm text-center">
          <p style={{ color: 'var(--text-secondary)' }}>
            Invalid reset link. Please request a new one.
          </p>
          <Link
            to="/auth/forgot-password"
            className="text-sm font-medium hover:underline mt-4 inline-block"
            style={{ color: 'var(--accent)' }}
          >
            Request new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold tracking-tight font-ui"
            style={{ color: 'var(--accent)' }}
          >
            Anvil
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Set a new password
          </p>
        </div>

        <Card
          classNames={{ base: 'border border-[var(--border)]' }}
          style={{ backgroundColor: 'var(--bg-surface)' }}
        >
          <CardBody className="p-6 gap-0">
            {success ? (
              <div className="flex flex-col gap-4">
                <div
                  className="text-sm px-3 py-2 rounded-lg border"
                  style={{
                    color: 'var(--green, #4ade80)',
                    backgroundColor: 'rgba(74, 222, 128, 0.08)',
                    borderColor: 'rgba(74, 222, 128, 0.2)',
                  }}
                >
                  Your password has been reset successfully.
                </div>
                <Link
                  to="/login"
                  className="flex items-center gap-1 text-sm font-medium hover:underline"
                  style={{ color: 'var(--accent)' }}
                >
                  <ArrowLeft size={14} />
                  Sign in with your new password
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* New Password */}
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="New Password"
                  placeholder="At least 8 characters"
                  value={password}
                  onValueChange={setPassword}
                  isRequired
                  autoComplete="new-password"
                  startContent={
                    <Lock
                      size={16}
                      style={{ color: 'var(--text-muted)', flexShrink: 0 }}
                    />
                  }
                  endContent={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="focus:outline-none"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff size={16} style={{ color: 'var(--text-muted)' }} />
                      ) : (
                        <Eye size={16} style={{ color: 'var(--text-muted)' }} />
                      )}
                    </button>
                  }
                  classNames={{
                    inputWrapper:
                      'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
                    input:
                      'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    label: 'text-[var(--text-secondary)] text-sm',
                  }}
                  variant="bordered"
                />

                {/* Password strength indicators */}
                {password.length > 0 && (
                  <div className="flex flex-col gap-1 -mt-2">
                    {PASSWORD_RULES.map((rule) => {
                      const passing = rule.test(password);
                      return (
                        <div
                          key={rule.label}
                          className="flex items-center gap-1.5 text-xs"
                          style={{
                            color: passing
                              ? 'var(--green, #4ade80)'
                              : 'var(--text-muted)',
                          }}
                        >
                          {passing ? <Check size={12} /> : <X size={12} />}
                          {rule.label}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Confirm Password */}
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onValueChange={setConfirmPassword}
                  isRequired
                  autoComplete="new-password"
                  startContent={
                    <Lock
                      size={16}
                      style={{ color: 'var(--text-muted)', flexShrink: 0 }}
                    />
                  }
                  endContent={
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="focus:outline-none"
                      tabIndex={-1}
                    >
                      {showConfirm ? (
                        <EyeOff size={16} style={{ color: 'var(--text-muted)' }} />
                      ) : (
                        <Eye size={16} style={{ color: 'var(--text-muted)' }} />
                      )}
                    </button>
                  }
                  classNames={{
                    inputWrapper:
                      'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
                    input:
                      'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    label: 'text-[var(--text-secondary)] text-sm',
                  }}
                  variant="bordered"
                />

                {/* Passwords don't match warning */}
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p
                    className="text-xs -mt-2"
                    style={{ color: 'var(--red)' }}
                  >
                    Passwords do not match
                  </p>
                )}

                {/* Error */}
                {error && (
                  <div
                    className="text-sm px-3 py-2 rounded-lg border"
                    style={{
                      color: 'var(--red)',
                      backgroundColor: 'rgba(248, 113, 113, 0.08)',
                      borderColor: 'rgba(248, 113, 113, 0.2)',
                    }}
                  >
                    {error}
                    {error.includes('expired') && (
                      <Link
                        to="/auth/forgot-password"
                        className="block mt-2 font-medium hover:underline"
                        style={{ color: 'var(--accent)' }}
                      >
                        Request a new reset link
                      </Link>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  isLoading={isLoading}
                  isDisabled={!canSubmit}
                  className="mt-1 font-medium"
                  style={{
                    backgroundColor: canSubmit
                      ? 'var(--accent)'
                      : 'var(--bg-panel)',
                    color: canSubmit ? '#0f1012' : 'var(--text-muted)',
                  }}
                  fullWidth
                >
                  {isLoading ? 'Resetting…' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
